import { supabase } from './client';

// Constants for storage buckets
export const STORAGE_BUCKETS = {
  DESIGNS: 'design-files',
  DOCUMENTS: 'documents',
  AVATARS: 'avatars'
} as const;

// Initialize storage buckets
export const initializeStorage = async () => {
  try {
    // First check if buckets exist
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    if (listError) throw listError;

    const existingBuckets = new Set(buckets?.map(b => b.name));

    // Only create buckets that don't exist
    if (!existingBuckets.has(STORAGE_BUCKETS.DESIGNS)) {
      const { error: designError } = await supabase.storage.createBucket(STORAGE_BUCKETS.DESIGNS, {
        public: false,
        fileSizeLimit: 50_000_000, // 50MB
        allowedMimeTypes: [
          'application/pdf',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'image/jpeg',
          'image/png',
          'application/dxf',
          'application/dwg'
        ]
      });
      if (designError && !designError.message.includes('already exists')) {
        throw designError;
      }
    }

    if (!existingBuckets.has(STORAGE_BUCKETS.DOCUMENTS)) {
      const { error: docError } = await supabase.storage.createBucket(STORAGE_BUCKETS.DOCUMENTS, {
        public: false,
        fileSizeLimit: 20_000_000 // 20MB
      });
      if (docError && !docError.message.includes('already exists')) {
        throw docError;
      }
    }

    if (!existingBuckets.has(STORAGE_BUCKETS.AVATARS)) {
      const { error: avatarError } = await supabase.storage.createBucket(STORAGE_BUCKETS.AVATARS, {
        public: true,
        fileSizeLimit: 5_000_000, // 5MB
        allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif']
      });
      if (avatarError && !avatarError.message.includes('already exists')) {
        throw avatarError;
      }
    }

    return true;
  } catch (error) {
    console.error('Error initializing storage buckets:', error);
    return false;
  }
};

// Function to upload design file
export const uploadDesignFile = async (
  file: File,
  designId: string,
  versionId: string
): Promise<{ path: string; error: Error | null }> => {
  try {
    if (!designId || !versionId) {
      throw new Error('Design ID and Version ID are required');
    }

    // Validate file size
    if (file.size > 50_000_000) {
      throw new Error('File size exceeds 50MB limit');
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'application/dxf',
      'application/dwg'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not allowed');
    }

    // Create safe filename
    const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filePath = `${designId}/${versionId}/${safeFileName}`;

    // Upload file with retries
    let attempts = 0;
    const maxAttempts = 3;
    let lastError: Error | null = null;

    while (attempts < maxAttempts) {
      try {
        const { data, error } = await supabase.storage
          .from(STORAGE_BUCKETS.DESIGNS)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) throw error;
        return { path: data.path, error: null };
      } catch (err) {
        lastError = err as Error;
        attempts++;
        if (attempts < maxAttempts) {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempts) * 1000));
        }
      }
    }

    throw lastError || new Error('Failed to upload file after multiple attempts');
  } catch (error) {
    console.error('Error uploading design file:', error);
    return { path: '', error: error as Error };
  }
};

// Function to get file URL
export const getFileUrl = async (
  bucket: keyof typeof STORAGE_BUCKETS,
  filePath: string | null
): Promise<string | null> => {
  try {
    if (!filePath) return null;

    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) throw error;
    return data?.signedUrl || null;
  } catch (error) {
    console.error('Error getting file URL:', error);
    return null;
  }
};

// Function to delete file
export const deleteFile = async (
  bucket: keyof typeof STORAGE_BUCKETS,
  filePath: string | null
): Promise<{ error: Error | null }> => {
  try {
    if (!filePath) {
      throw new Error('File path is required');
    }

    const { error } = await supabase.storage
      .from(bucket)
      .remove([filePath]);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting file:', error);
    return { error: error as Error };
  }
};